import { useContext, useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { excApi } from '@exchange-frontends/api';
import { useIMS } from '@exchange-frontends/custom-hooks';

import { ContainerContext } from '../../../utils';
import { useStore } from '../store';

/**
 * Uses SWR to fetch and cache reviews for the given asset id.
 * It also interacts with the Zustand store to set `totalReviews` values.
 * @param {string} assetId ID of the plugin you want the reviews for
 * @param {string} offset
 */
export const useReviews = (assetId, offset, accessToken, userId) => {
  const rnrEndpointsLoading = useStore((store) => store.rnrEndpointsLoading);

  const { ims } = useContext(ContainerContext);
  const imsDataInitialized = ims?.initialized && (!ims?.isSignedInUser() || (userId && accessToken));
  const shouldFetch = imsDataInitialized && !rnrEndpointsLoading;
  const { data, error } = useSWR(
    shouldFetch ? ['get_reviews', assetId, offset] : null,
    (_key, assetId, offset) =>
      excApi.getReviews({
        accessToken,
        userId,
        assetId,
        offset,
      }),
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (data) {
      useStore.setState({ totalReviews: data.pageSpec?.totalItems });
    }
  }, [data]);

  return {
    reviews: data?.items,
    totalReviews: data?.pageSpec?.totalItems,
    error: error,
    isLoading: !data && !error,
  };
};

/**
 * Uses SWR to fetch and cache current user review.
 * @param {string} accessToken User's IMS access token
 * @param {string} imsUserId User's IMS user id
 * @param {string} assetId ID of the plugin you want the reviews for
 * @param {bool} shouldFetch It will fetch if this is true
 */
export const useMyReview = (accessToken, imsUserId, assetId, shouldFetch) => {
  const rnrEndpointsLoading = useStore((store) => store.rnrEndpointsLoading);
  const ims = useIMS();
  const { data, error } = useSWR(
    shouldFetch && !rnrEndpointsLoading ? ['get_my_review', accessToken, imsUserId, assetId] : null,
    (key, accessToken, imsUserId, assetId) => excApi.getMyReview({ accessToken, imsUserId, assetId }),
    {
      revalidateOnFocus: false,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // Never retry on 404.
        if (error.code === 404) return;

        if (error.code === 401) {
          ims.signIn();
          return;
        }

        console.log(error);
        // Only retry up to 5 times.
        if (retryCount >= 5) return;

        // Retry after 2 seconds.
        setTimeout(() => revalidate({ retryCount }), 2000);
      },
    }
  );

  useEffect(() => {
    if (data) {
      useStore.setState({ currentUserReview: data });
    }
  }, [data]);

  return {
    data: data,
    error: error,
    isLoading: !data && !error,
  };
};

export const useMatchMutate = () => {
  const { cache, mutate } = useSWRConfig();
  return (matcher, ...args) => {
    if (!(cache instanceof Map)) {
      throw new Error('matchMutate requires the cache provider to be a Map instance');
    }

    const keys = [];

    for (const key of cache.keys()) {
      // console.log('checked key: ' + key);
      if (matcher.test(key)) {
        // console.debug('matched key: ' + key);
        keys.push(key);
      }
    }

    const mutations = keys.map((key) => mutate(key, ...args));
    return Promise.all(mutations);
  };
};
